import { Cart } from "@sections/cart";
import { IAgeGate } from "@snippets/age-gate";

export interface IMainCart extends HTMLElement {
  changeCartItemQuantity(id: string | number, quantity: number): void;
  handleUpdateCartSection(): void;
}

class MainCart extends Cart implements IMainCart {
  public cartSectionId: string | null = null;
  private boundHandleUpdateCartSection: () => void;
  private currentNote: string = "";
  private isUpdatingInput: boolean = false;

  constructor() {
    super();
    this.handleAgeGate = this.handleAgeGate.bind(this);
    this.boundHandleUpdateCartSection = this.handleUpdateCartSection.bind(this);

    window.setTimeout(() => {
      this.classList.remove("is-loading");
    }, 1500);
  }

  connectedCallback(): void {
    this.handleInitialization();
    super.connectedCallback();
  }

  private handleInitialization(): void {
    this.setCartSectionId();
    this.setEventListeners();
    this.initGiftMessage();
  }

  private setCartSectionId(): void {
    const sectionWrapper = this.closest(".shopify-section") as HTMLDivElement;

    if (!sectionWrapper) {
      console.error("Cart: Cart section not found.");
      return;
    }

    const idString = sectionWrapper.id;
    const sectionId = idString.replace("shopify-section-", "");

    this.cartSectionId = sectionId;
  }

  private setEventListeners(): void {
    this.cleanupEventListeners();

    document.addEventListener(
      "cart:updated",
      this.boundHandleUpdateCartSection,
    );

    const ageGatedCheckoutForm = this.querySelector('[js-main-cart="form"]');
    if (ageGatedCheckoutForm) {
      ageGatedCheckoutForm.addEventListener(
        "submit",
        this.handleAgeGate.bind(this),
      );
    }
  }

  private cleanupEventListeners(): void {
    document.removeEventListener(
      "cart:updated",
      this.boundHandleUpdateCartSection,
    );

    const ageGatedCheckoutForm = this.querySelector('[js-main-cart="form"]');
    if (ageGatedCheckoutForm) {
      ageGatedCheckoutForm.removeEventListener(
        "submit",
        this.handleAgeGate.bind(this),
      );
    }
  }

  // Function to remove emojis using comprehensive regex pattern
  private stripEmojis(text: string): string {
    if (!text) return "";

    // Comprehensive regex to match standard and Windows emojis
    const emojiRegex =
      /[\u{1F000}-\u{1FFFF}]|[\u{2600}-\u{27BF}]|[\u{2300}-\u{23FF}]|[\u{2B50}]|[\u{2B55}]|[\u{200D}]|[\u{FE0F}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA00}-\u{1FAFF}]|[\u{2702}-\u{27B0}]|[\u{24C2}-\u{1F251}]|[\u{1F000}-\u{1FFFD}]|[\u{1F3FB}-\u{1F3FF}]|[\u{1F9B0}-\u{1F9B3}]|[\u{2700}-\u{27BF}]|[\u{2600}-\u{26FF}]/gu;

    // First pass with regex
    const originalText = text;
    let cleanedText = text.replace(emojiRegex, "");

    // Second pass - check for high Unicode values
    const chars = cleanedText.split("");
    for (let i = 0; i < chars.length; i++) {
      // Remove any character with code point >= 8000 (most emojis and special symbols)
      if (chars[i].charCodeAt(0) >= 8000) {
        chars[i] = "";
      }
    }
    cleanedText = chars.join("");

    // Check if any emojis were removed
    if (originalText !== cleanedText) {
      // If we detected and removed emojis, display the warning
      this.showEmojiWarning();
      return cleanedText;
    }

    return cleanedText;
  }

  // Function to show emoji warning
  private showEmojiWarning(): void {
    const warningElement = document.querySelector(
      ".gift-message-emoji-error",
    ) as HTMLElement;
    if (warningElement) {
      warningElement.style.display = "block";

      // Automatically hide after 5 seconds
      setTimeout(() => {
        warningElement.style.display = "none";
      }, 5000);
    } else {
      // If warning element doesn't exist yet, create it dynamically
      const noteContainer = document.querySelector(".main-cart__note");
      if (noteContainer) {
        const emojiWarning = document.createElement("div");
        emojiWarning.className = "gift-message-emoji-error";
        emojiWarning.innerHTML = `
          <p>
            Emojis are not supported and have been removed from your text.
          </p>
        `;
        emojiWarning.style.display = "block";
        emojiWarning.style.marginTop = "10px";
        emojiWarning.style.marginBottom = "10px";
        emojiWarning.style.padding = "10px 15px";
        emojiWarning.style.backgroundColor = "#fff2f2";
        emojiWarning.style.border = "1px solid #ff5252";
        emojiWarning.style.borderRadius = "4px";
        emojiWarning.style.color = "#ff0000";
        emojiWarning.style.fontWeight = "bold";
        emojiWarning.style.fontSize = "14px";
        emojiWarning.style.zIndex = "100";

        // Insert after the note container
        noteContainer.insertAdjacentElement("afterend", emojiWarning);

        // Auto-hide after 5 seconds
        setTimeout(() => {
          emojiWarning.style.display = "none";
        }, 5000);
      }
    }
  }

  private initGiftMessage(): void {
    const noteContainer = document.querySelector(".main-cart__note");
    const noteInput = document.querySelector(
      ".main-cart__note-input",
    ) as HTMLInputElement;
    const existingButton = document.querySelector(
      '[js-main-cart="noteAddButton"]',
    ) as HTMLButtonElement;

    if (!noteContainer || !noteInput) return;

    this.currentNote = noteInput.value || "";

    // Hide the existing plus button
    if (existingButton) {
      existingButton.style.display = "none";
    }

    // Create elements as siblings to the note container
    const formContainer = noteContainer.parentElement;
    if (!formContainer) return;

    // Create character counter
    this.createCharCounter(noteInput, formContainer, noteContainer);

    // Create buttons container
    this.createButtonsContainer(formContainer, noteContainer);

    // Create message display area
    this.createMessageDisplay(formContainer, noteContainer);

    // Update initial state
    this.updateUIState();

    // Handle input changes with emoji blocking
    noteInput.addEventListener("input", (e) => {
      // Skip if we're programmatically updating the input
      if (this.isUpdatingInput) return;

      const target = e.target as HTMLInputElement;
      const originalValue = target.value;

      // Remove emojis
      const cleanedValue = this.stripEmojis(originalValue);

      if (cleanedValue !== originalValue) {
        // Update input value if emojis were removed
        target.value = cleanedValue;
      }

      // Enforce 128 character limit
      if (target.value.length > 128) {
        target.value = target.value.substring(0, 128);
      }

      // Update counter
      this.updateCharCounter(target);

      // Update UI state
      this.updateUIState();
    });

    // Handle paste events with emoji blocking
    noteInput.addEventListener("paste", (event: ClipboardEvent) => {
      const input = event.target as HTMLInputElement;
      if (!input) return;

      // Define proper type for Window with clipboardData
      interface WindowWithClipboard extends Window {
        clipboardData?: DataTransfer;
      }

      const clipboardData =
        event.clipboardData || (window as WindowWithClipboard).clipboardData;
      const pastedText = clipboardData?.getData("text") || "";

      const cleanedValue = this.stripEmojis(pastedText);
      if (cleanedValue !== pastedText) {
        // Prevent the default paste
        event.preventDefault();

        // Insert at cursor position
        const startPos = input.selectionStart || 0;
        const endPos = input.selectionEnd || 0;
        const beforeText = input.value.substring(0, startPos);
        const afterText = input.value.substring(endPos);

        const newValue = beforeText + cleanedValue + afterText;

        // Enforce 128 character limit
        const finalValue =
          newValue.length > 128 ? newValue.substring(0, 128) : newValue;
        input.value = finalValue;

        // Set cursor position after the inserted text
        const newCursorPos = Math.min(startPos + cleanedValue.length, 128);
        input.selectionStart = input.selectionEnd = newCursorPos;

        // Update counter and UI state
        this.updateCharCounter(input);
        this.updateUIState();
      }
    });

    // Handle button clicks
    this.attachButtonListeners();
  }

  private createCharCounter(
    noteInput: HTMLInputElement,
    parent: Element,
    insertAfter: Element,
  ): void {
    const charCounter = document.querySelector(".gift-message-counter");
    if (!charCounter) {
      const counterEl = document.createElement("div");
      counterEl.className = "gift-message-counter";
      counterEl.textContent = `${noteInput.value.length}/128`;
      insertAfter.insertAdjacentElement("beforebegin", counterEl);
    }
  }

  private createButtonsContainer(parent: Element, insertAfter: Element): void {
    const buttonsContainer = document.querySelector(".gift-message-buttons");
    if (!buttonsContainer) {
      const buttonsEl = document.createElement("div");
      buttonsEl.className = "gift-message-buttons";

      // Get the trash icon from window.themeVars.icons
      const trashIcon = window.themeVars?.icons?.trash || "";

      buttonsEl.innerHTML = `
        <button type="button" class="gift-message-add btn btn-black btn-small">
          Save
        </button>
        <div class="gift-message-actions" style="display: none;">
          <button type="button" class="gift-message-update btn btn-black btn-small">
            Update
          </button>
          <button type="button" class="gift-message-remove btn btn-black btn-small">
            ${trashIcon}
          </button>
        </div>
      `;

      // Insert after the note container (not after character counter)
      insertAfter.insertAdjacentElement("afterend", buttonsEl);
    }
  }

  private createMessageDisplay(parent: Element, insertAfter: Element): void {
    const messageDisplay = document.querySelector(".gift-message-display");
    if (!messageDisplay) {
      const displayEl = document.createElement("div");
      displayEl.className = "gift-message-display";
      displayEl.style.display = "none";
      displayEl.innerHTML = `
        <div class="gift-message-preview">
          <strong>Gift Message:</strong>
          <span class="gift-message-text"></span>
        </div>
      `;

      // Insert after the buttons container
      const buttonsContainer = document.querySelector(".gift-message-buttons");
      if (buttonsContainer) {
        buttonsContainer.insertAdjacentElement("afterend", displayEl);
      } else {
        insertAfter.insertAdjacentElement("afterend", displayEl);
      }
    }
  }

  private attachButtonListeners(): void {
    document.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;

      // Find the button element (in case user clicked on SVG or path inside button)
      const button = target.closest(
        ".gift-message-add, .gift-message-update, .gift-message-remove",
      ) as HTMLButtonElement;

      if (button) {
        e.preventDefault();

        if (button.classList.contains("gift-message-add")) {
          this.addGiftMessage();
        } else if (button.classList.contains("gift-message-update")) {
          this.updateGiftMessage();
        } else if (button.classList.contains("gift-message-remove")) {
          this.removeGiftMessage();
        }
      }
    });
  }

  private updateUIState(): void {
    const noteInput = document.querySelector(
      ".main-cart__note-input",
    ) as HTMLInputElement;
    const addButton = document.querySelector(
      ".gift-message-add",
    ) as HTMLButtonElement;
    const actionsContainer = document.querySelector(
      ".gift-message-actions",
    ) as HTMLElement;
    const messageDisplay = document.querySelector(
      ".gift-message-display",
    ) as HTMLElement;
    const messageText = document.querySelector(
      ".gift-message-text",
    ) as HTMLElement;
    const updateButton = document.querySelector(
      ".gift-message-update",
    ) as HTMLButtonElement;
    const cartContainer = document.querySelector(".main-cart") as HTMLElement;

    if (
      !noteInput ||
      !addButton ||
      !actionsContainer ||
      !messageDisplay ||
      !messageText ||
      !updateButton ||
      !cartContainer
    )
      return;

    const hasCurrentNote = this.currentNote.trim().length > 0;
    const hasInputValue = noteInput.value.trim().length > 0;

    if (hasCurrentNote) {
      // There is a saved message - show message display and action buttons
      // Populate input with current message if it's empty (for editing)
      if (!hasInputValue) {
        this.isUpdatingInput = true;
        noteInput.value = this.currentNote;
        this.updateCharCounter(noteInput);
        this.isUpdatingInput = false;
      }

      addButton.style.display = "none";
      actionsContainer.style.display = "flex";
      messageDisplay.style.display = "block";
      messageText.textContent = this.currentNote;

      // Update button is always enabled when there's a saved message
      updateButton.disabled = false;

      // Add class to indicate buttons are visible
      cartContainer.classList.add("has-visible-gift-buttons");
    } else {
      // No saved message - only show add button when there's input
      if (hasInputValue) {
        addButton.style.display = "block";
        addButton.disabled = false;
        // Add class to indicate buttons are visible
        cartContainer.classList.add("has-visible-gift-buttons");
      } else {
        addButton.style.display = "none";
        // Remove class when no buttons are visible
        cartContainer.classList.remove("has-visible-gift-buttons");
      }

      actionsContainer.style.display = "none";
      messageDisplay.style.display = "none";
    }
  }

  private updateCharCounter(noteInput: HTMLInputElement): void {
    const charCounter = document.querySelector(".gift-message-counter");
    if (charCounter) {
      charCounter.textContent = `${noteInput.value.length}/128`;
    }
  }

  private addGiftMessage(): void {
    const noteInput = document.querySelector(
      ".main-cart__note-input",
    ) as HTMLInputElement;
    if (noteInput && noteInput.value.trim()) {
      this.currentNote = noteInput.value.trim();
      super.addNote(this.currentNote);
      this.updateUIState();
    }
  }

  private updateGiftMessage(): void {
    const noteInput = document.querySelector(
      ".main-cart__note-input",
    ) as HTMLInputElement;
    if (noteInput && noteInput.value.trim()) {
      this.currentNote = noteInput.value.trim();
      super.addNote(this.currentNote);
      this.updateUIState();
    }
  }

  private removeGiftMessage(): void {
    const noteInput = document.querySelector(
      ".main-cart__note-input",
    ) as HTMLInputElement;
    if (noteInput) {
      // Clear both the input field and saved message
      this.isUpdatingInput = true;
      noteInput.value = "";
      this.currentNote = "";

      // Update the character counter
      this.updateCharCounter(noteInput);
      this.isUpdatingInput = false;

      // Save empty note to cart
      super.addNote("");

      // Update UI to reflect changes
      this.updateUIState();
    }
  }

  public changeCartItemQuantity(id: string | number, quantity: number): void {
    super.changeItemQuantity(id, quantity);
  }

  public async handleUpdateCartSection() {
    try {
      this.cleanupEventListeners();

      const existingUpsellElement = this.querySelector("uwp-cart-upsell");

      const url = `${window.themeVars.routes.cart}?section_id=${this.cartSectionId}`;
      const response = await fetch(url);
      const text = await response.text();
      const html = new DOMParser().parseFromString(text, "text/html");

      const newSection = html.querySelector("uwp-main-cart");
      if (!newSection) {
        console.error("Cart: New section not found.");
        return;
      }

      this.innerHTML = newSection.innerHTML;

      // If 'uwp-cart-upsell' exists, re-insert the original element
      if (existingUpsellElement) {
        const newCartUpsellElement = this.querySelector("uwp-cart-upsell");
        if (newCartUpsellElement) {
          newCartUpsellElement.replaceWith(existingUpsellElement);
        } else {
          this.appendChild(existingUpsellElement);
        }
      }

      this.handleInitialization();

      setTimeout(() => {
        const cartEmpty = this.cartData.item_count === 0;
        if (cartEmpty) {
          this.classList.add("is-empty");
          return;
        }
        this.classList.remove("is-empty");
      }, 50);

      window.setTimeout(() => {
        this.classList.remove("is-loading");
      }, 1500);
    } catch (error: unknown) {
      console.error(error);
    }
  }

  private handleAgeGate(event: Event | undefined): void {
    event?.preventDefault();

    const ageGate = document.querySelector(
      "uwp-age-gate[modal-name='age-gate']",
    ) as IAgeGate;

    const isLocalStorageSet = ageGate?.isAgeApproved() || false;

    if (isLocalStorageSet) {
      this.redirectToCheckout();
      return;
    }

    document.addEventListener("ageGate:approved", () =>
      this.redirectToCheckout(),
    );

    ageGate?.showAgeGate();
  }

  private redirectToCheckout(): void {
    window.location.href = "/checkout";
  }
}

customElements.define("uwp-main-cart", MainCart);
